import React from 'react';
import { X, Shield, Eye, Lock, Database, Globe, Users, FileText } from 'lucide-react';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
     const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  if (!isOpen) return null;

  return (
    <div
      onClick={handleBackdropClick}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-300"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden transform animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 p-6 text-white overflow-hidden">
          <button
            onClick={onClose}
            className="absolute top-1 right-5 p-2 !z-[9999] hover:bg-white/20 rounded-xl transition-all duration-200 hover:scale-110"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="relative z-10 flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2">Privacy Policy</h2>
              <p className="text-blue-100">
                QCBT Platform - Effective Date: January 1, 2025
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-8">
          <div className="prose prose-slate max-w-none">
            {/* Introduction */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-8 border border-blue-200">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-blue-900 m-0">
                  Introduction
                </h3>
              </div>
              <p className="text-blue-800 m-0">
                QCBT Platform ("we," "our," or "us") is committed to protecting
                your privacy. This Privacy Policy explains how we collect, use,
                disclose, and safeguard your information when you use our
                computer-based testing platform and related services.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Database className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">
                  Information We Collect
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-2">
                    Personal Information
                  </h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Name, email address, phone number</li>
                    <li>• Educational institution details</li>
                    <li>• Account credentials and preferences</li>
                    <li>• Payment and billing information</li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-2">
                    Examination Data
                  </h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Test responses and scores</li>
                    <li>• Exam session recordings (video/audio)</li>
                    <li>• Biometric data for verification</li>
                    <li>• Browser and device information</li>
                  </ul>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-5 h-5 text-amber-600" />
                  <h4 className="font-semibold text-amber-800 m-0">
                    AI Proctoring Data
                  </h4>
                </div>
                <p className="text-amber-700 text-sm m-0">
                  Our AI proctoring system may collect facial recognition data,
                  eye movement patterns, and behavioral analytics during
                  examinations for integrity purposes.
                </p>
              </div>
            </div>

            {/* How We Use Information */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">
                  How We Use Your Information
                </h3>
              </div>

              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-slate-800">
                    Service Provision
                  </h4>
                  <p className="text-slate-600 text-sm">
                    To provide, maintain, and improve our testing platform,
                    process examinations, and deliver results to authorized
                    institutions.
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-slate-800">
                    Security & Integrity
                  </h4>
                  <p className="text-slate-600 text-sm">
                    To prevent cheating, verify identity, monitor exam sessions,
                    and maintain the integrity of the testing environment.
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-slate-800">
                    Communication
                  </h4>
                  <p className="text-slate-600 text-sm">
                    To send important notifications about your account, exams,
                    technical updates, and customer support communications.
                  </p>
                </div>
              </div>
            </div>

            {/* Data Security */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">
                  Data Security
                </h3>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Lock className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-green-800">Encryption</h4>
                    <p className="text-green-700 text-sm">
                      End-to-end encryption for all data transmission
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-green-800">
                      SOC 2 Compliance
                    </h4>
                    <p className="text-green-700 text-sm">
                      Industry-standard security controls
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Database className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-green-800">
                      Secure Storage
                    </h4>
                    <p className="text-green-700 text-sm">
                      Protected cloud infrastructure
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Sharing */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Information Sharing
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <p className="font-medium text-slate-800">
                      Educational Institutions
                    </p>
                    <p className="text-sm text-slate-600">
                      We share exam results and performance data with your
                      registered institution.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <p className="font-medium text-slate-800">
                      Service Providers
                    </p>
                    <p className="text-sm text-slate-600">
                      Third-party vendors who assist in providing our services
                      under strict confidentiality agreements.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <p className="font-medium text-slate-800">
                      Legal Requirements
                    </p>
                    <p className="text-sm text-slate-600">
                      When required by law, court order, or to protect our
                      rights and safety.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Your Rights */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Your Rights
              </h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">
                      Access & Portability
                    </h4>
                    <p className="text-blue-700 text-sm">
                      Request access to your personal data and receive a copy in
                      a portable format.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">
                      Correction
                    </h4>
                    <p className="text-blue-700 text-sm">
                      Request correction of inaccurate or incomplete personal
                      information.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">
                      Deletion
                    </h4>
                    <p className="text-blue-700 text-sm">
                      Request deletion of your personal data, subject to legal
                      and contractual obligations.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">
                      Opt-out
                    </h4>
                    <p className="text-blue-700 text-sm">
                      Opt-out of certain data processing activities and
                      marketing communications.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">
                  Contact Us
                </h3>
              </div>
              <p className="text-slate-700 mb-4">
                If you have questions about this Privacy Policy or our data
                practices, please contact us:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="font-medium text-slate-800">Email</p>
                  <p className="text-slate-600">cbt@msqube.com</p>
                </div>
                <div>
                  <p className="font-medium text-slate-800">Phone</p>
                  <p className="text-slate-600">033-4814-4250</p>
                </div>
                <div>
                  <p className="font-medium text-slate-800">Whatsapp</p>
                  <p className="text-slate-600">033-4814-4250</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};